/**
 * Turn one scanned barcode into the thing it identifies.
 *
 * The warehouse scans exactly two kinds of label:
 *
 *   Product  a QR holding a deep link, `https://prism-commerce.com/i/<id>`,
 *            with the same id printed underneath in human-readable form.
 *            Built by `buildProductLabelZpl` (workers/src/services/zpl.js).
 *   Location a Code128 of a tote or shelf id, `LIT-1-A-1-1-6`. Tote codes and
 *            shelf codes share one flat namespace — nothing downstream needs
 *            to know which of the two it is holding.
 *
 * Pure and synchronous: it decides what a string *is*, never what to do about
 * it. Resolving an item or validating a location against Prism belongs to the
 * caller. Anything unrecognised comes back as `unknown` with the raw scan
 * intact, so the UI can show the operator what the gun actually read.
 */

// Must match DEEP_LINK_BASE_URL in workers/src/routes/labels.js — a printed QR
// outlives any redeploy, so this pair can only ever be changed together.
const DEEP_LINK_HOST = 'prism-commerce.com'
const DEEP_LINK_PATH_RE = /^\/i\/(\d{1,20})\/?$/

// Mirrors app/utils/item_id.py: 0, 1, I and O are excluded from the alphabet
// precisely so they cannot be misread off a label.
const ITEM_ID_PREFIX = 'AT-'
const ITEM_ID_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
const ITEM_ID_LENGTH = 8
const ITEM_ID_RE = new RegExp(`^${ITEM_ID_PREFIX}[${ITEM_ID_ALPHABET}]{${ITEM_ID_LENGTH}}$`)

// Ported verbatim from app/utils/shopify_sku.py. The leading `[A-Za-z]*` and
// the trailing boundary absorb the item-suffix carried by Shopify SKUs
// (`LIT-1-A-1-1-6-NSH75P`, `LIT-1-D-3-4-1_vhfkyxhe`).
const LOCATION_RE = /^[A-Za-z]*(LIT-\d+-[A-Z]+-\d+-\d+-\d+)(?:[-_ ]|$)/i

const SHOPIFY_PRODUCT_ID_RE = /^\d{1,20}$/

/**
 * @param {string|null|undefined} text  Raw characters from the scanner or keyboard.
 * @returns {{kind: 'item', by: 'shopify_product_id'|'item_id', value: string, raw: string}
 *          |{kind: 'location', value: string, raw: string}
 *          |{kind: 'unknown', raw: string}}
 */
export function parseScan(text) {
    const raw = text == null ? '' : String(text)
    const trimmed = raw.trim()
    if (!trimmed) return { kind: 'unknown', raw }

    const deepLinkId = readDeepLink(trimmed)
    if (deepLinkId) return { kind: 'item', by: 'shopify_product_id', value: deepLinkId, raw }

    const upper = trimmed.toUpperCase()
    if (ITEM_ID_RE.test(upper)) return { kind: 'item', by: 'item_id', value: upper, raw }

    const location = LOCATION_RE.exec(trimmed)
    if (location) return { kind: 'location', value: location[1].toUpperCase(), raw }

    if (SHOPIFY_PRODUCT_ID_RE.test(trimmed)) {
        return { kind: 'item', by: 'shopify_product_id', value: trimmed, raw }
    }

    return { kind: 'unknown', raw }
}

/** The product id from one of our own deep links, or null for anything else. */
function readDeepLink(text) {
    let url
    try {
        url = new URL(text)
    } catch {
        return null
    }
    if (url.hostname.replace(/^www\./i, '').toLowerCase() !== DEEP_LINK_HOST) return null
    const match = DEEP_LINK_PATH_RE.exec(url.pathname)
    return match ? match[1] : null
}
